import { ErrorStreamChange } from './../models/$error.model';
import { Action, createAction, createActionGroup, createFeature, createFeatureSelector, createReducer, emptyProps, on } from '@ngrx/store';
import { createEntityAdapter, EntityState, Update } from '@ngrx/entity';
import { Session, ProgramDay, Program, DateSchedule } from 'globals/store/programs.data';
import { userActions } from './user.era';
import { eventsActions } from './events.era';
export { Session, ProgramDay, Program, DateSchedule }


export const LOAD_PROGRAMS = "[Programs] Load Programs"
export const PROGRAM_LOADED = "[Programs] Loaded"
export const CLEAR_PROGRAMS = "[Programs] Clear Programs"
export const PROGRAM_DATES = "[Programs] Programs dates have changed";
export const PROGRAM_DATES_BY_USER = "[Programs] User changed program dates";
export const PROGRAM_DATES_UPDATED_EVENT_DATES = "[Programs] Now event dates match program dates";
export const DAY_ADDED_TO_PROGRAM = "[Programs] day added to program index"
export const CREATE = '[Programs] create';
export const UPDATE = '[Programs] update';
export const DELETE = '[Programs] delete';
// ...for firebase
export const ADD_ONE  = '[Programs] one added from db';
export const ADD_MANY = '[Programs] many added from db'
export const MODIFIED = '[Programs] modified from db';
export const MODIFIED_MANY = '[Programs] batch modified from db';
export const REMOVED  = '[Programs] removed from db';
export const SUCCESS  = '[Programs] end loading';
export const FOCUS_FIRST_PROGRAM = '[Programs] FOCUS_FIRST_PROGRAM'

/**
 * Created by greg on 20/07/2018.
 */

export interface ScheduleProgramDay extends ProgramDay {
	time?: string
	type?: string
}

export interface ProgramTimeline extends Program {
	day: Date
	scheduleId: any
	sessions?: Array<ScheduleProgramDay>
}

export interface NewDay {
  program_idx:number
  program_displaying:boolean
}

export interface ScrollLevel {
    tick: number;
    percentage: number;
}

export interface ProgramsState extends EntityState<Program> {
  loaded:boolean;
  loading:boolean | string
  has_date: boolean
  user_changed_dates: boolean
  date_arr: string[]
  new_day_added: NewDay | undefined
  focusFirstProgram: boolean
}

const makeInitialState = (): ProgramsState => ({
  loaded:false,
  loading:true,
  has_date:false,
  user_changed_dates:false,
  ids: [],
  date_arr: [],
  entities: {},
  new_day_added: undefined,
  focusFirstProgram: false
})
// Entity adapter
export const adapter = createEntityAdapter<Program>({sortComparer:(a:Program, b:Program)=>a.order > b.order ? 1 : -1});

export const programsActions = createActionGroup({
  source: 'Programs',
  events: {
    'Load Programs': emptyProps(),
    'Program Dates Changed': (dates_arr:string[], changed_by_user?:boolean) => ({ dates_arr, changed_by_user }),
    'Program Dates Updated To Match Event Dates': emptyProps(),
    'Clear Programs': emptyProps(),
    'Program Session Day Added': (payload: Partial<NewDay>) => ({ payload }),
    'Create Program': (program: Program, log_data: Partial<ErrorStreamChange>) => ({ program, log_data }),
    'Update Program': (id: string, changes: Partial<Program>, log_data: Partial<ErrorStreamChange>) => ({ id, changes, log_data }),
    'Delete Program': (id: string, log_data: Partial<ErrorStreamChange>) => ({ id, log_data }),
    'Add One Program': (program: Program) => ({ program }),
    'Add Many Programs': (payload: Program[]) => ({ payload }),
    'Modify Program': (id: string, changes: Partial<Program>) => ({ id, changes }),
    'Modify Many Programs': (payload: Update<Program>[]) => ({ payload }),
    'Remove Program': (id: string) => ({ id }),
    'Program Success': emptyProps(),
    'Focus First Program': (focusFirstProgram: boolean) => ({ focusFirstProgram })
    }
  }
)


const hasDates = (state:ProgramsState, updates:Program[]) => [...Object.values(state.entities), ...updates].filter(p=>p.date !== undefined).length > 0

export const programsReducer = createReducer(
  { ...makeInitialState() },
  on(programsActions.focusFirstProgram, (state, { focusFirstProgram }) => ({ ...state, focusFirstProgram })),
  on(userActions.logout, (state) => makeInitialState()),
  on(eventsActions.unloadEvent, (state) => makeInitialState()),
  on(programsActions.clearPrograms, (state) => makeInitialState()),
  on(programsActions.programDatesChanged, (state, { dates_arr, changed_by_user }) => {
    const user_changed_dates = changed_by_user === false || changed_by_user === true ? changed_by_user : state.user_changed_dates;
    return {...state, date_arr: dates_arr, user_changed_dates};
  }),
  on(programsActions.programDatesUpdatedToMatchEventDates, (state) => ({ ...state, user_changed_dates:false })),
  on(programsActions.programSessionDayAdded, (state, { payload }) => ({ ...state, new_day_added: { program_idx: payload.program_idx, program_displaying: payload.program_displaying || false }})),
  on(programsActions.createProgram, (state, { program, log_data }) => {
    // TODO log the creation with log_data
    return adapter.addOne(program, {...state, loading:true});
  }),
  on(programsActions.updateProgram, (state, { id, changes, log_data }) => {
    // TODO log the update with log_data
    const newState = adapter.updateOne({
      id,
      changes,
  }, {...state, loading:id})
    newState.has_date = hasDates(newState, [])
    return newState
  }),
  on(programsActions.deleteProgram, (state, { id, log_data }) => {
    // TODO log the deletion with log_data
    const newState = adapter.removeOne(id, {...state, loading:id})
    newState.has_date = hasDates(newState, [])
    return newState
  }),
  on(programsActions.addOneProgram, (state, { program }) => {
    const has_date = hasDates(state, [program])
    return adapter.addOne(program, {...state, has_date, loaded:true, loading:false });
  }),
  on(programsActions.addManyPrograms, (state, { payload }) => {
    const has_date = hasDates(state, payload)
    return adapter.addMany(payload, {...state, loaded:true, has_date, loading:false  })
  }),
  on(programsActions.modifyProgram, (state, { id, changes }) => {
    const temp_prog = {...state.entities[id], ...changes}
    const has_date = hasDates(state, [temp_prog])
    return adapter.updateOne({
        id,
        changes
      }, {...state, loading:false, has_date, loaded:true})
    }),
  on(programsActions.modifyManyPrograms, (state, { payload }) => {
    return adapter.updateMany(payload, {...state, loading:false, loaded:true});
  }),
  on(programsActions.removeProgram, (state, { id }) => {
    const newState = adapter.removeOne(id, {...state, loading:false})
    newState.has_date = hasDates(newState, [])
    return newState
  }),
  on(programsActions.programSuccess, (state) => ({ ...state, loaded:true, loading: false }))
)

export const programsFeature = createFeature({
  name: 'programs',
  reducer: programsReducer,
});

export const programsStateSelector = createFeatureSelector<ProgramsState>('programs');

